import { useState } from 'react';
import { useUserContext } from '../../hooks/useUserContext';
import { userService, type CreateUserWorkHistoryDto, type UpdateUserWorkHistoryDto } from '../../services/userService';
import { track } from '../../services/analytics';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
  Alert,
} from '@mui/material';
import AddOutlined from '@mui/icons-material/AddOutlined';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CpaxLogo from '../../components/CpaxLogo';

const steps = ['Background', 'Confirmation'];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 50 }, (_, i) => currentYear - i);

interface WorkEntry {
  id: number;
  backendId: string | null;
  companyName: string;
  jobTitle: string;
  industry: string;
  startMonth: string;
  startYear: string;
  isCurrent: boolean;
  endMonth: string;
  endYear: string;
  description: string;
}

function createEmptyEntry(id: number): WorkEntry {
  return {
    id,
    backendId: null,
    companyName: '',
    jobTitle: '',
    industry: '',
    startMonth: '',
    startYear: '',
    isCurrent: false,
    endMonth: '',
    endYear: '',
    description: '',
  };
}

const INDUSTRIES = [
  'Accounting & Finance',
  'Audit & Assurance',
  'Banking & Financial Services',
  'Consulting',
  'Education',
  'Government & Public Sector',
  'Healthcare',
  'Insurance',
  'Legal',
  'Manufacturing',
  'Non-Profit',
  'Real Estate',
  'Technology',
  'Other',
];

export default function IndividualBackgroundManualPage() {
  const navigate = useNavigate();
  const location = useLocation();
  useUserContext();
  const planData = location.state?.planData;
  const profileData = location.state?.profileData;
  // Restore entries when navigating back from the confirmation page
  const initialEntries: WorkEntry[] = location.state?.workEntries ?? [createEmptyEntry(1)];
  const [entries, setEntries] = useState<WorkEntry[]>(initialEntries);
  const [nextId, setNextId] = useState(
    initialEntries.length > 0 ? Math.max(...initialEntries.map((e) => e.id)) + 1 : 2
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const updateEntry = (id: number, field: keyof WorkEntry, value: string | boolean) => {
    setEntries((prev) =>
      prev.map((e) => {
        if (e.id !== id) return e;
        const updated = { ...e, [field]: value };
        // clear end date when switching to current
        if (field === 'isCurrent' && value === true) {
          updated.endMonth = '';
          updated.endYear = '';
        }
        return updated;
      })
    );
  };

  const addEntry = () => {
    setEntries((prev) => [...prev, createEmptyEntry(nextId)]);
    setNextId((n) => n + 1);
  };

  const removeEntry = (id: number) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const handleBack = () => {
    navigate('/auth/individual-background', { state: { planData, profileData } });
  };

  /** Convert month name + year string to an ISO datetime string. */
  const toIsoDate = (month: string, year: string): string => {
    const monthIndex = MONTHS.indexOf(month) + 1;
    return `${year}-${String(monthIndex).padStart(2, '0')}-01T00:00:00Z`;
  };

  const handleContinue = async () => {
    setSaveError(null);
    setIsSaving(true);
    try {
      const savedEntries = await Promise.all(
        entries.map(async (entry) => {
          const startDate = entry.startMonth && entry.startYear
            ? toIsoDate(entry.startMonth, entry.startYear)
            : new Date().toISOString();
          const endDate = entry.isCurrent || !entry.endMonth || !entry.endYear
            ? null
            : toIsoDate(entry.endMonth, entry.endYear);

          if (entry.backendId) {
            const dto: UpdateUserWorkHistoryDto = {
              companyName: entry.companyName,
              jobTitle: entry.jobTitle,
              industry: entry.industry,
              startDate,
              endDate,
              isCurrent: entry.isCurrent,
              description: entry.description,
            };
            const result = await userService.updateWorkHistoryItem(entry.backendId, dto);
            return { ...entry, backendId: result.id };
          } else {
            const dto: CreateUserWorkHistoryDto = {
              companyName: entry.companyName,
              jobTitle: entry.jobTitle,
              industry: entry.industry,
              startDate,
              endDate,
              isCurrent: entry.isCurrent,
              description: entry.description,
            };
            const result = await userService.createWorkHistoryItem(dto);
            return { ...entry, backendId: result.id };
          }
        })
      );
      setEntries(savedEntries);
      track('background_info_completed', { method: 'manual' });
      navigate('/auth/individual-background-confirmation', {
        state: { workEntries: savedEntries, planData, profileData },
      });
    } catch (err) {
      console.error('Failed to save work history:', err);
      setSaveError('Failed to save work history. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'surface.subtle',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top AppBar — logo only */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: 1,
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CpaxLogo height={28} showMark={false} />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Stepper AppBar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 }, justifyContent: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 852 }}>
            <Stepper activeStep={0}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main content — centered */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 4, sm: 8 },
          px: { xs: 2, sm: 0 },
          pb: { xs: '80px', sm: '128px' },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 608,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 24, sm: 32 },
              fontWeight: 400,
              lineHeight: 1.235,
              letterSpacing: '0.25px',
              color: 'primary.main',
              textAlign: 'center',
              mb: 1,
            }}
          >
            Add your work history
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              lineHeight: 1.43,
              letterSpacing: '0.17px',
              color: 'text.primary',
              textAlign: 'center',
              mb: 4,
            }}
          >
            Add your most recent positions to help us personalize your CPE recommendations.
          </Typography>

          {/* Work entry cards */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            {entries.map((entry, index) => (
              <Paper
                key={entry.id}
                variant="outlined"
                sx={{
                  width: '100%',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                {/* Card header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    pt: 2,
                    pb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: 500,
                      lineHeight: 1.6,
                      letterSpacing: '0.15px',
                      color: 'text.primary',
                    }}
                  >
                    {index === 0 ? 'Current / Most Recent Position' : `Position ${index + 1}`}
                  </Typography>
                  {entries.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeEntry(entry.id)}
                      aria-label="Remove entry"
                      sx={{ color: 'action.active' }}
                    >
                      <DeleteOutlined />
                    </IconButton>
                  )}
                </Box>

                <Divider />

                {/* Fields */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                  {/* Company + Title row */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Company Name"
                      variant="outlined"
                      fullWidth
                      value={entry.companyName}
                      onChange={(e) => updateEntry(entry.id, 'companyName', e.target.value)}
                    />
                    <TextField
                      label="Job Title"
                      variant="outlined"
                      fullWidth
                      value={entry.jobTitle}
                      onChange={(e) => updateEntry(entry.id, 'jobTitle', e.target.value)}
                    />
                  </Box>

                  {/* Industry */}
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Industry</InputLabel>
                    <Select
                      label="Industry"
                      value={entry.industry}
                      onChange={(e) => updateEntry(entry.id, 'industry', e.target.value)}
                    >
                      {INDUSTRIES.map((ind) => (
                        <MenuItem key={ind} value={ind}>
                          {ind}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Start date */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Start Month</InputLabel>
                      <Select
                        label="Start Month"
                        value={entry.startMonth}
                        onChange={(e) => updateEntry(entry.id, 'startMonth', e.target.value)}
                      >
                        {MONTHS.map((m) => (
                          <MenuItem key={m} value={m}>
                            {m}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Start Year</InputLabel>
                      <Select
                        label="Start Year"
                        value={entry.startYear}
                        onChange={(e) => updateEntry(entry.id, 'startYear', e.target.value)}
                      >
                        {YEARS.map((y) => (
                          <MenuItem key={y} value={String(y)}>
                            {y}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Currently employed checkbox */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={entry.isCurrent}
                        onChange={(e) => updateEntry(entry.id, 'isCurrent', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="I currently work here"
                  />

                  {/* End date — hidden when current */}
                  {!entry.isCurrent && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>End Month</InputLabel>
                        <Select
                          label="End Month"
                          value={entry.endMonth}
                          onChange={(e) => updateEntry(entry.id, 'endMonth', e.target.value)}
                        >
                          {MONTHS.map((m) => (
                            <MenuItem key={m} value={m}>
                              {m}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>End Year</InputLabel>
                        <Select
                          label="End Year"
                          value={entry.endYear}
                          onChange={(e) => updateEntry(entry.id, 'endYear', e.target.value)}
                        >
                          {YEARS.map((y) => (
                            <MenuItem key={y} value={String(y)}>
                              {y}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* Description */}
                  <TextField
                    label="Responsibilities & Achievements"
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={3}
                    value={entry.description}
                    onChange={(e) => updateEntry(entry.id, 'description', e.target.value)}
                    helperText="Brief summary of your role and key responsibilities"
                  />
                </Box>
              </Paper>
            ))}

            {/* Add another position */}
            <Button
              variant="outlined"
              startIcon={<AddOutlined />}
              onClick={addEntry}
              sx={{
                alignSelf: 'flex-start',
                borderColor: 'rgba(86, 151, 55, 0.5)',
                fontWeight: 500,
                letterSpacing: '0.46px',
              }}
            >
              Add Another Position
            </Button>

            {/* Save error */}
            {saveError && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {saveError}
              </Alert>
            )}
          </Box>
        </Box>
      </Box>

      {/* Bottom footer bar — fixed at bottom */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: 'auto',
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          height: { xs: 'auto', sm: 128 },
        }}
      >
        <Toolbar
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 0 },
            justifyContent: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="outlined"
            size="large"
            onClick={handleBack}
            sx={{
              px: '22px',
              py: 1,
              fontSize: '0.9375rem',
              fontWeight: 500,
              letterSpacing: '0.46px',
              borderColor: 'rgba(86, 151, 55, 0.5)',
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={handleContinue}
            disabled={isSaving}
            sx={{
              px: '22px',
              py: 1,
              fontSize: '0.9375rem',
              fontWeight: 500,
              letterSpacing: '0.46px',
            }}
          >
            {isSaving ? 'Saving…' : 'Continue'}
          </Button>
        </Toolbar>
      </AppBar>
    </Box>
  );
}
